import { useState, useCallback, useMemo } from "react";
import { KeyMasterAction } from "../services/KeyMasterService";
import { KeyMasterDataType, Realm, RealmLower, NotifyFn, KEYMASTER_LABELS, NAV } from "../constants";
import { HashParams } from "../utils";

export interface UseKeyMasterProps {
  onNotify: NotifyFn;
  convertFn: (value: string, dataType: string, usePrefix: boolean, action: KeyMasterAction, realm: RealmLower) => Promise<string>;
}

export interface UseKeyMasterReturn {
  realm: Realm;
  setRealm: (r: Realm) => void;
  action: KeyMasterAction;
  dataType: KeyMasterDataType;
  setDataType: (d: KeyMasterDataType) => void;
  usePrefix: boolean;
  setUsePrefix: (p: boolean) => void;
  inputValue: string;
  setInputValue: (v: string) => void;
  outputValue: string;
  loading: boolean;
  realmLower: RealmLower;
  handleConvert: () => Promise<void>;
  handleSwap: () => void;
  inputLabel: string;
  outputLabel: string;
  selectedAction: { value: string; label: string };
}

const parseUrlState = (): { realm?: Realm; dataType?: KeyMasterDataType } => {
  const params = HashParams.parse();
  return {
    realm: (params.get("realm")?.toUpperCase() as Realm) || undefined,
    dataType: (params.get("dataType") as KeyMasterDataType) || undefined,
  };
};

const updateUrlState = (realm: Realm, dataType: KeyMasterDataType): void => {
  HashParams.update(NAV.ROUTES.KEYMASTER, {
    realm: realm.toLowerCase(),
    dataType,
  });
};

export function useKeyMaster({ onNotify, convertFn }: UseKeyMasterProps): UseKeyMasterReturn {
  const urlState = useMemo(parseUrlState, []);
  const [realm, setRealmState] = useState<Realm>(urlState.realm ?? "NA");
  const [action, setAction] = useState<KeyMasterAction>("ENCRYPT");
  const [dataType, setDataTypeState] = useState<KeyMasterDataType>(urlState.dataType ?? KeyMasterDataType.FULL_NAME);
  const [usePrefix, setUsePrefix] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [outputValue, setOutputValue] = useState("");
  const [loading, setLoading] = useState(false);

  const realmLower = useMemo(() => realm.toLowerCase() as RealmLower, [realm]);

  const setRealm = useCallback(
    (r: Realm) => {
      setRealmState(r);
      updateUrlState(r, dataType);
    },
    [dataType]
  );

  const setDataType = useCallback(
    (d: KeyMasterDataType) => {
      setDataTypeState(d);
      updateUrlState(realm, d);
    },
    [realm]
  );

  const handleConvert = useCallback(async (): Promise<void> => {
    if (!inputValue.trim()) {
      return;
    }

    setLoading(true);
    try {
      const result = await convertFn(inputValue, dataType, usePrefix, action, realmLower);
      setOutputValue(result);
      onNotify("success", action === "ENCRYPT" ? KEYMASTER_LABELS.SUCCESS_ENCRYPTED : KEYMASTER_LABELS.SUCCESS_DECRYPTED);
    } catch (err) {
      onNotify("error", `${KEYMASTER_LABELS.CONVERSION_FAILED}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [inputValue, dataType, usePrefix, action, realmLower, onNotify, convertFn]);

  const handleSwap = useCallback((): void => {
    setAction((prev) => (prev === "ENCRYPT" ? "DECRYPT" : "ENCRYPT"));
    setInputValue(outputValue);
    setOutputValue("");
  }, [outputValue]);

  const inputLabel = action === "ENCRYPT" ? KEYMASTER_LABELS.PLAIN_TEXT : KEYMASTER_LABELS.ENCRYPTED_TEXT;
  const outputLabel = action === "ENCRYPT" ? KEYMASTER_LABELS.ENCRYPTED_TEXT : KEYMASTER_LABELS.PLAIN_TEXT;
  const selectedAction = useMemo(() => ({ value: action, label: action }), [action]);

  return {
    realm,
    setRealm,
    action,
    dataType,
    setDataType,
    usePrefix,
    setUsePrefix,
    inputValue,
    setInputValue,
    outputValue,
    loading,
    realmLower,
    handleConvert,
    handleSwap,
    inputLabel,
    outputLabel,
    selectedAction,
  };
}
